/**
 * Indian-number-formatted rupee helper. Accepts paise (integer) and returns
 * a properly-grouped string like `₹1,23,456` or `₹1,23,456.50`.
 *
 * Uses the built-in Indian locale grouping so we don't reinvent it.
 */
export function formatInr(paise, { withSymbol = true, showPaise = false } = {}) {
    if (paise === null || paise === undefined || Number.isNaN(paise)) return withSymbol ? "₹0" : "0";
    const rupees = Number(paise) / 100;
    const formatter = new Intl.NumberFormat("en-IN", {
        style: withSymbol ? "currency" : "decimal",
        currency: "INR",
        minimumFractionDigits: showPaise ? 2 : 0,
        maximumFractionDigits: showPaise ? 2 : 0,
    });
    return formatter.format(rupees);
}

/** Parse a rupee-text input into paise. Accepts "1,234.50" / "₹499" / "499" */
export function rupeesToPaise(input) {
    if (input === null || input === undefined || input === "") return null;
    const cleaned = String(input).replace(/[₹,\s]/g, "");
    const num = Number(cleaned);
    if (!Number.isFinite(num)) return null;
    return Math.round(num * 100);
}

/** Short big-number formatter: 1234 → 1.2k, 1,230,000 → 12.3L (Indian) */
export function compactInr(paise) {
    const rupees = Number(paise || 0) / 100;
    if (rupees >= 10_000_000) return `₹${(rupees / 10_000_000).toFixed(1)}Cr`;
    if (rupees >= 100_000) return `₹${(rupees / 100_000).toFixed(1)}L`;
    if (rupees >= 1000) return `₹${(rupees / 1000).toFixed(1)}k`;
    return `₹${Math.round(rupees)}`;
}

/** Human date from an ISO string. `undefined` → "—". */
export function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

export function formatDateTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}
